import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box, Button, styled, withStyles,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from '@hapi/joi';
import formErrorMessage from '@/configs/formErrorMessage';
import config from '@/configs/config';
import { BirthdayField, EmailField, GenderField } from '../../user/components/Profile';
import { GENDER } from '../../user/const';
import { isCampaignOngoing } from '../../ooha202304/utils';

export const STEP = {
  ENTER_PHONENUBMER: 'ENTER_PHONENUBMER',
  ENTER_VERIFICATION_CODE: 'ENTER_VERIFICATION_CODE',
  ENTER_PROFILE: 'ENTER_PROFILE',
  VERIFY_PHONE: 'VERIFY_PHONE',
  CONNECT_THIRD_PARTY_ACCOUNT: 'CONNECT_THIRD_PARTY_ACCOUNT',
  FINISH_REGISTER: 'FINISH_REGISTER',
};

const schema = Joi.object({
  phoneNumber: Joi.string()
    .pattern(/^09\d{8}$/, 'phoneNumber')
    .required()
    .messages({
      'string.pattern.name': '您輸入的手機號碼格式錯誤',
    }),
  verificationCode: Joi.string()
    .pattern(/^\d{6}$/, 'verificationCode')
    .required()
    .messages({
      'string.pattern.name': '請輸入六位數字驗證碼',
    }),
});

const profilSchema = Joi.object({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .messages({ 'string.empty': '明細寄送信箱為必填' }),
  gender: Joi
    .string()
    .valid(GENDER.MALE, GENDER.FEMALE)
    .messages({
      'string.empty': '稱謂為必填',
      'any.only': '稱謂必須為先生或是小姐',
    })
    .required(),
  year: Joi.number().integer().min(1900).max(9999)
    .messages({ 'number.base': '年份為必填' })
    .required(),
  month: Joi.number().integer().min(1).max(12)
    .messages({ 'number.base': '月份為必填' })
    .required(),
  day: Joi.number().integer().min(1).max(31)
    .messages({ 'number.base': '日期為必填' })
    .required(),
}).and('year', 'month', 'day');

const StyledInput = styled('input')({
  background: 'transparent',
  border: 0,
  fontSize: 20,
  fontWeight: 'bold',
  height: '100%',
  margin: 0,
  outline: 0,
  padding: '6px 22px',
  width: '100%',
  boxShadow: '0px 0px 8px #bfbfbf80',
  '&:disabled': {
    boxShadow: 'none',
    backgroundColor: '#e4e4e4',
  },
});

export const StyledButton = withStyles({
  root: {
    backgroundColor: '#00b900',
    borderRadius: 9,
    flexShrink: 0,
    fontSize: 16,
    fontWeight: 'bold',
    height: '100%',
    minWidth: 34,
    width: '100%',
    '&:hover': {
      backgroundColor: '#00b900',
    },
    '&:active': {
      opacity: '.5',
    },
  },
  label: {
    color: 'white',
  },
  disabled: {
    backgroundColor: '#e4e4e4',
  },
})(Button);

const SendButton = withStyles({
  disabled: {
    backgroundColor: 'rgb(190, 190, 190)',
  },
})(StyledButton);

const Tab = ({ children, isActivate }) => (
  <Box
    alignItems="center"
    bgcolor={isActivate ? 'rgb(102,102,102)' : 'rgb(228,228,228)'}
    color="white"
    component="span"
    display="flex"
    flexBasis={0}
    flexGrow={1}
    flexShrink={0}
    justifyContent="center"
  >
    {children}
  </Box>
);

export const ListItem = ({ children }) => (
  <Box display="flex" alignItems="center">
    <Box mx={1.5} width={13} height={13} bgcolor="success.main" borderRadius="50%" />
    <Box component="span">{children}</Box>
  </Box>
);

const VerifyPhoneStep = ({
  onSendVeriyCodeButtonClick,
  onRegisterButtonClick,
  setFormStep,
}) => {
  const [currentStep, setCurrentStep] = useState(STEP.ENTER_PHONENUBMER);
  const {
    register,
    getValues,
    formState: { errors },
    trigger,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '09',
      verificationCode: '',
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });
  const formPhoneNumber = watch('phoneNumber');
  const [countDownInterverl, setCountDownInterverl] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState('');
  const [countDown, setCountDown] = useState(0);

  useEffect(() => () => {
    window.clearInterval(countDownInterverl);
  }, [countDownInterverl]);

  const startCountDown = useCallback(() => {
    let count = 180;

    setCountDown(count);

    const interval = window.setInterval(() => {
      setCountDown(count -= 1);
      if (count === 0) window.clearInterval(interval);
    }, 1000);

    setCountDownInterverl(interval);
  }, [setCountDown, setCountDownInterverl]);
  return (
    <Box width={1} display="flex" flexDirection="column" mt={1.5} alignItems="stretch">
      <Box ml={1.25} color="grey.600">輸入電話號碼</Box>
      <Box
        alignItems="center"
        bgcolor="white"
        borderRadius={9}
        display="flex"
        height={37}
        mt={0.75}
        position="relative"
        width={1}
      >
        <Box flexGrow={1}>
          <StyledInput
            disabled={currentStep !== STEP.ENTER_PHONENUBMER}
            inputMode="numeric"
            maxLength={10}
            name="phoneNumber"
            ref={register}
          />
        </Box>
        <Box position="absolute" height={26} right={17}>
          <SendButton
            style={{
              fontSize: 13,
            }}
            disableRipple
            disabled={formPhoneNumber.length !== 10 || countDown !== 0}
            onClick={async () => {
              const isValid = await trigger('phoneNumber');
              const phoneNumber = getValues('phoneNumber');

              if (!isValid) return;

              const result = await onSendVeriyCodeButtonClick(phoneNumber);

              if (!result) return;

              setCurrentStep(STEP.ENTER_VERIFICATION_CODE);
              startCountDown();
            }}
          >
            {currentStep === STEP.ENTER_PHONENUBMER ? '發送驗證碼' : `再次發送${countDown ? `(${countDown})` : ''}`}
          </SendButton>
        </Box>
      </Box>
      <Box ml={1.5} mt={1.5} color="grey.600">輸入驗證碼</Box>
      <Box display="flex" mt={0.5} height={37}>
        <Box flexGrow={1} flexShrink={1} flexBasis={0} bgcolor="white" borderRadius={6}>
          <StyledInput
            disabled={currentStep !== STEP.ENTER_VERIFICATION_CODE}
            inputMode="numeric"
            maxLength={6}
            name="verificationCode"
            ref={register}
          />
        </Box>
        <Box flexGrow={1} flexShrink={1} flexBasis={0} ml={1} borderRadius={6}>
          <StyledButton
            disableRipple
            disabled={currentStep !== STEP.ENTER_VERIFICATION_CODE}
            onClick={async () => {
              const isValid = await trigger();
              const phoneNumber = getValues('phoneNumber');
              const verificationCode = getValues('verificationCode');

              if (!isValid) return;

              const result = await onRegisterButtonClick(phoneNumber, verificationCode);

              if (typeof result === 'string') {
                setErrorMessage(result);
                return;
              }
              if (!result) return;

              setFormStep(STEP.ENTER_PROFILE);
            }}
          >
            註冊
          </StyledButton>
        </Box>
      </Box>
      <Box height={28} color="red" ml={1.5} pt={0.75}>
        <Box color="red" fontSize={10}>
          {
            errorMessage || Object.values(errors || {}).map((error) => error.message).join(', ')
          }
        </Box>
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box component="span">註冊即表示我同意</Box>
        <a
          href={config.TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          隱私政策和服務條款
        </a>
        <Box component="span">。</Box>
      </Box>
    </Box>
  );
};

const ConnectLineStep = ({ onConnectLineClick }) => (
  <Box mt={4}>
    <StyledButton onClick={() => {
      onConnectLineClick();
    }}
    >
      LINE登入並加入官方帳號
    </StyledButton>
  </Box>
);

const FinishRegisterStep = ({ afterLoginUrl = '/' }) => {
  const history = useHistory();
  const location = useLocation();
  const { state } = location;
  const [countDown, setCountDown] = useState(10);

  useEffect(() => {
    let count = 15;

    const countDownTimout = window.setTimeout(() => {
      history.push(afterLoginUrl, {
        ...(state || {}),
      });
    }, 15000);

    const countDownInterverl = window.setInterval(() => {
      setCountDown(count -= 1);
    }, 1000);

    return () => {
      window.clearTimeout(countDownTimout);
      window.clearInterval(countDownInterverl);
    };
  }, [afterLoginUrl, history, state]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={2.25}>
      {isCampaignOngoing() && (
      <Box height={42} width={1}>
        <StyledButton
          style={{
            backgroundColor: '#ff7701',
          }}
          onClick={() => {
            window.open('https://line.me/R/ti/p/@freezone');
          }}
        >
          <span style={{ color: '#000000' }}>
            到LINE確認OOHA活動資格
          </span>
        </StyledButton>
      </Box>
      )}
      {
    (() => {
      if (config.modules?.['discount_button'] === false) return null;

      return (
        <Box height={42} width={1} mt={1}>
          <StyledButton
            style={{
              backgroundColor: 'rgb(255,200,33)',
            }}
            onClick={() => {
              history.push('/faq');
            }}
          >
            <span style={{ color: '#000000' }}>
              立即使用7折優惠
            </span>
          </StyledButton>
        </Box>
      );
    })()
  }
      {
        (config.modules?.['pre-orders'] === false) ? null : (
          <Box height={42} width={1} mt={1}>
            <StyledButton
              style={{
                backgroundColor: 'rgb(255,200,33)',
              }}
              onClick={() => {
                history.push('/menu');
              }}
            >
              <span style={{ color: '#000000' }}>
                查看菜單
              </span>
            </StyledButton>
          </Box>
        )
      }
      <Box height={42} width={1} mt={1}>
        <StyledButton
          style={{
            backgroundColor: 'rgb(255,200,33)',
          }}
          onClick={() => {
            history.push('/user/profile');
          }}
        >
          <span style={{ color: '#000000' }}>
            綁定發票載具
          </span>
        </StyledButton>
      </Box>
      <Box mt={1.5} fontSize={10}>
        本畫面將在
        {' '}
        {countDown}
        {' '}
        秒後自動跳轉
      </Box>
    </Box>
  );
};

const ProfileStep = ({
  isConnectPhone,
  onNextButtonClick,
  setFormStep,
}) => {
  const {
    control,
    errors,
    getValues,
    trigger,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      email: '',
      year: '',
      month: '',
      day: '',
      gender: '',
    },
    resolver: joiResolver(profilSchema, {
      abortEarly: false,
      messages: {
        ...formErrorMessage,
        'number.max': '年份、月份或是日期格式有誤',
        'number.min': '年份、月份或是日期格式有誤',
      },
    }),
  });

  return (
    <Box mt={1.5}>
      <BirthdayField
        control={control}
        labelWidth={60}
        errors={errors}
      />
      <Controller
        control={control}
        name="gender"
        render={({ ref, ...props }) => (
          <GenderField
            labelWidth={60}
            ref={ref}
            errors={errors}
            {...props}
          />
        )}
      />
      <EmailField
        control={control}
        labelWidth={60}
        errors={errors}
      />
      <StyledButton onClick={async () => {
        const isValid = await trigger();

        if (!isValid) return;

        onNextButtonClick(getValues());

        if (isConnectPhone) {
          setFormStep(STEP.FINISH_REGISTER);
        } else {
          setFormStep(STEP.CONNECT_THIRD_PARTY_ACCOUNT);
        }
      }}
      >
        下一步
      </StyledButton>
    </Box>
  );
};

const Form = ({
  afterLoginUrl,
  isConnectPhone,
  isFinishRegister,
  isMgm,
  onAuthButtonClick,
  onConnectLineClick,
  onLoginClick,
  onNextButtonClick,
  onProfileNextButtonClick,
}) => {
  const [currentStep, setCurrentStep] = useState(
    isFinishRegister
      ? STEP.FINISH_REGISTER
      : STEP.VERIFY_PHONE
  );
  const [isShowLoginButton, setIsShowLoginButton] = useState(true);
  const onSendVeriyCodeButtonClick = useCallback(async (phoneNumber) => {
    const result = await onNextButtonClick(phoneNumber);

    if (result) setIsShowLoginButton(false);

    return result;
  }, [onNextButtonClick, setIsShowLoginButton]);

  return (
    <Box
      alignItems="stretch"
      bgcolor="grey.200"
      borderRadius={{ xs: '24px 24px 0 0', sm: 24 }}
      display="flex"
      flexDirection="column"
      fontSize={12}
      pb={3}
    >
      <Box py={2.75} textAlign="center">
        <Box fontSize={20} fontWeight="bold">
          { currentStep === STEP.VERIFY_PHONE && !isConnectPhone && (config.headerForVerifyingPhoneStep || '')}
          { currentStep === STEP.ENTER_PROFILE && !isConnectPhone && (config.headerForEnteringPhoneStep || '')}
          { (currentStep === STEP.CONNECT_THIRD_PARTY_ACCOUNT) && 'LINE登入後即完成註冊'}
          { (currentStep === STEP.VERIFY_PHONE && isConnectPhone) && 'LINE登入後即完成註冊'}
          { (currentStep === STEP.ENTER_PHONENUBMER && isConnectPhone) && 'LINE登入後即完成註冊'}
          { currentStep === STEP.FINISH_REGISTER && '恭喜您！成功加入會員！'}
        </Box>
      </Box>
      <Box height={25} display="flex" width={1}>
        <Tab isActivate={
            currentStep === STEP.VERIFY_PHONE
            || currentStep === STEP.ENTER_PROFILE
          }
        >
          Step 1.
          {' '}
          {(currentStep === STEP.VERIFY_PHONE || currentStep === STEP.ENTER_PROFILE)
            ? '輸入基本資料'
            : '完成'}
        </Tab>
        <Tab isActivate={currentStep === STEP.CONNECT_THIRD_PARTY_ACCOUNT}>
          Step 2.
          {' '}
          {isConnectPhone ? (
            'LINE登入完成'
          ) : (
            <>
              {currentStep === STEP.CONNECT_THIRD_PARTY_ACCOUNT && 'LINE 登入'}
              {currentStep === STEP.FINISH_REGISTER && '完成'}
            </>
          )}
        </Tab>
      </Box>
      <Box mt={1.5} px={4.5} width={1} display="flex" flexDirection="column" alignItems="stretch">
        <Box width={1}>
          {currentStep === STEP.VERIFY_PHONE && (
            (config.introductionTitles || []).map((title) => <Box key={title}>{title}</Box>)
          )}
          {currentStep === STEP.CONNECT_THIRD_PARTY_ACCOUNT && `使用LINE登入並加入${config.BrandNameTW}LINE官方帳號，即可完成註冊，立即享受以下服務：`}
          {currentStep === STEP.FINISH_REGISTER && `歡迎加入!請點選下方按鈕，開始探索你的${config.BrandNameTW}吧:)`}
        </Box>
        {currentStep !== STEP.FINISH_REGISTER
        && (
        <Box width={1} display="flex" flexDirection="column" mt={1.25} alignItems="stretch">
          {(config.introductionItems || {}).map((item) => <ListItem key={item}>{item}</ListItem>)}
        </Box>
        )}
        {currentStep === STEP.VERIFY_PHONE && (
          <VerifyPhoneStep
            isConnectPhone={isConnectPhone}
            onRegisterButtonClick={onAuthButtonClick}
            onSendVeriyCodeButtonClick={onSendVeriyCodeButtonClick}
            setFormStep={setCurrentStep}
          />
        )}
        {currentStep === STEP.ENTER_PROFILE && (
          <ProfileStep
            onNextButtonClick={onProfileNextButtonClick}
            isConnectPhone={isConnectPhone}
            setFormStep={setCurrentStep}
          />
        )}
        {currentStep === STEP.CONNECT_THIRD_PARTY_ACCOUNT && !isConnectPhone && (
          <ConnectLineStep
            onConnectLineClick={onConnectLineClick}
            setFormStep={setCurrentStep}
          />
        )}
        {currentStep === STEP.FINISH_REGISTER && (
          <FinishRegisterStep afterLoginUrl={afterLoginUrl} />
        )}
        {currentStep === STEP.VERIFY_PHONE && !isConnectPhone && !isMgm && (
          <>
            <Box display="flex" alignItems="center" mt={2}>
              <Box borderTop={1} flexGrow={1} borderColor="grey.600" />
              <Box
                component="span"
                color={isShowLoginButton ? 'grey.600' : '#3f51b5'}
                mx={1.5}
                onClick={() => {
                  setIsShowLoginButton(true);
                }}
              >
                會員快速登入
              </Box>
              <Box borderTop={1} flexGrow={1} borderColor="grey.600" />
            </Box>
            {isShowLoginButton
            && (
            <Box flexGrow={1} mt={2} borderRadius={6} height={37}>
              <StyledButton
                disableRipple
                onClick={async () => {
                  onLoginClick();
                }}
              >
                會員快速登入
              </StyledButton>
            </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Form;
